import React, { useEffect, useMemo, useState } from 'react';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import Modal, { ModalButton } from '../../../ui/Modal';

function StatusBadge({ name }) {
  const n = String(name || '').toLowerCase();
  let tone = 'neutral';
  if (/создан|формир/.test(n)) tone = 'warning';
  else if (/в пути|выдан/.test(n)) tone = 'info';
  else if (/закрыт|завершен/.test(n)) tone = 'success';
  return <span className={`crmpage__status crmpage__status--${tone}`}>{name || '—'}</span>;
}

function formatDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function AddToWaybillModal({ isOpen, onClose, onConfirm }) {
  const [waybills, setWaybills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId(null);
    setSearch('');
    setErr('');
    fetchWaybills();
    // eslint-disable-next-line
  }, [isOpen]);

  const fetchWaybills = async () => {
    try {
      setLoading(true);
      setErr('');
      const res = await fetch(`${process.env.REACT_APP_API_URL}v1/waybills?sortBy=updated_at&orderBy=desc`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setWaybills(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.message || 'Не удалось загрузить путевые');
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return waybills;
    return waybills.filter(w =>
      String(w.id).includes(q) ||
      (w.driver_name || '').toLowerCase().includes(q) ||
      (w.vehicle_name || '').toLowerCase().includes(q) ||
      (w.region_name || '').toLowerCase().includes(q) ||
      (w.waybill_status_name || '').toLowerCase().includes(q)
    );
  }, [waybills, search]);

  const handleConfirm = () => {
    if (!selectedId) return;
    const wb = waybills.find(w => w.id === selectedId);
    if (wb) onConfirm?.(wb);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Добавить к путевому листу"
      subtitle="Выберите путевой лист — заказы будут привязаны к нему"
      size="lg"
      footer={
        <>
          <ModalButton variant="ghost" onClick={onClose}>Отмена</ModalButton>
          <ModalButton variant="primary" onClick={handleConfirm} disabled={!selectedId || loading}>
            <PostAddOutlinedIcon sx={{ fontSize: 18 }} />
            Добавить
          </ModalButton>
        </>
      }
    >
      <div className="uimodal-field" style={{ gridTemplateColumns: '1fr', paddingTop: 0 }}>
        <input
          type="text"
          placeholder="Поиск: №, экспедитор, транспорт, регион, статус…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%', height: 38, padding: '0 12px',
            border: '1px solid rgba(56,47,42,0.18)', borderRadius: 8,
            fontFamily: 'inherit', fontSize: 13, background: '#fff', outline: 'none',
          }}
        />
      </div>

      {err && (
        <div style={{
          margin: '12px 0', padding: '10px 12px',
          background: 'rgba(220,77,69,0.08)',
          border: '1px solid rgba(220,77,69,0.25)',
          color: '#B73C36', borderRadius: 8, fontSize: 13,
        }}>{err}</div>
      )}

      <table className="uimodal-table" style={{ marginTop: 12 }}>
        <thead>
          <tr>
            <th style={{ width: 30 }}></th>
            <th>№</th>
            <th>Статус</th>
            <th>Экспедитор</th>
            <th>Транспорт</th>
            <th>Регион</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#8C8077' }}>Загрузка…</td></tr>
          ) : filtered.length === 0 ? (
            <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#8C8077' }}>Нет данных</td></tr>
          ) : (
            filtered.map(wb => (
              <tr key={wb.id} onClick={() => setSelectedId(wb.id)}
                  style={{ cursor: 'pointer', background: selectedId === wb.id ? 'rgba(240,143,90,0.08)' : undefined }}>
                <td>
                  <input
                    type="radio" name="wbpick"
                    checked={selectedId === wb.id}
                    onChange={() => setSelectedId(wb.id)}
                    style={{ accentColor: '#F08F5A' }}
                  />
                </td>
                <td><span className="crmpage__num">№{wb.id}</span></td>
                <td><StatusBadge name={wb.waybill_status_name} /></td>
                <td>{wb.driver_name || '—'}</td>
                <td>{wb.vehicle_name || '—'}{wb.registration_number ? ` (${wb.registration_number})` : ''}</td>
                <td>{wb.region_name || '—'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </Modal>
  );
}
